import React, {useMemo, useState} from "react"
import { useTranslation } from "react-i18next"
import {useDispatch} from 'react-redux'
import {useSnackbar} from 'notistack'

//@ts-ignore
import Dialog from "../../../../components/Dialog"
import { getCompany } from "../../../../store/features/companySlice"
import { CompanyUserRoleCode } from "../../../../types/userRoles"
import { useTracFloSelector } from "../../../../store/useTracFloSelector"
import DataGridAccordion from "../../../../components/DataGridAccordion"
import {
  addCustomCode,
  getCompanyCustomCodes,
  getCustomCodes,
  updateCustomCode,
} from '../../../../store/features/customCodeSlice'
import CodesListColumns from './CodesList.columns'
import CreateEditCode, {CreateEditCodeValues, createInitialValues} from '../../../../forms/CreateEditCode'
import {createCompanyCode, editCompanyCode} from '../../../../api/customCode'
import {CustomCode} from '../../../../types/customCode'


function userCanEditRow(companyUserRole: CompanyUserRoleCode): boolean {
  return companyUserRole !== "crew"
}

type CodesListDialog = 'edit' | null

export default function CodesList() {
  const { t } = useTranslation("private")
  const dispatch = useDispatch()
  const {enqueueSnackbar} = useSnackbar()
  const allCodes = useTracFloSelector(getCustomCodes)
  const company = useTracFloSelector(getCompany)
  const [dialog, setDialog] = useState<CodesListDialog>(null)
  const [editCc, setEditCc] = useState<CustomCode | null>(null)

  const closeDialog = () => {
    setDialog(null)
    setEditCc(null)
  }

  const handleEdit = useMemo(() => {
    return async (cc: CustomCode): Promise<void> => {
      setEditCc(cc)
      setDialog('edit')
    }
  }, [setEditCc, setDialog])

  const submitCreate = useMemo(() => {
    return async (values: CreateEditCodeValues) => {
      // before submitting, make sure that this code is not currently being used
      const findCode = allCodes.find((cc) => cc.code === values.code)
      if (findCode != null) {
        enqueueSnackbar(t("view.Settings.CodesList.createDuplicateCode"), {
          variant: "error",
        })
        return
      }
      try {
        const {data} = await createCompanyCode(values)
        if (!!data?.id) {
          dispatch(addCustomCode(data))
          enqueueSnackbar(t("view.Settings.CodesList.createSuccess"), {
            variant: "success",
          })
        } else {
          enqueueSnackbar(t("view.Settings.CodesList.createFail"), {
            variant: "error",
          })
        }
      } catch (e) {
        enqueueSnackbar(t("view.Settings.CodesList.createFail"), {
          variant: "error",
        })
      }
    }
  }, [dispatch, enqueueSnackbar, t, allCodes])

  const submitEdit = useMemo(() => {
    return async (values: CreateEditCodeValues) => {
      // early return if we dont have an object set
      if (editCc == null) return
      // before submitting, make sure that this code is not currently being used (by non-edited)
      const findCode = allCodes.find((cc) => cc.code === values.code && cc.id !== editCc.id)
      if (findCode != null) {
        enqueueSnackbar(t("view.Settings.CodesList.editDuplicateCode"), {
          variant: "error",
        })
        return
      }
      try {
        const {data} = await editCompanyCode(editCc.id, values)
        if (!!data?.id) {
          dispatch(updateCustomCode(data))
          enqueueSnackbar(t("view.Settings.CodesList.editSuccess"), {
            variant: "success",
          })
        } else {
          enqueueSnackbar(t("view.Settings.CodesList.editFail"), {
            variant: "error",
          })
        }
      } catch (e) {
        enqueueSnackbar(t("view.Settings.CodesList.editFail"), {
          variant: "error",
        })
      }
    }
  }, [dispatch, enqueueSnackbar, t, allCodes, editCc])

  const rows = useMemo(() => {
    return getCompanyCustomCodes(allCodes)
  }, [allCodes])

  const columns = useMemo(() => {
    return CodesListColumns({
      editable: userCanEditRow(company.company_user_role),
      handleEdit,
      t,
    })
  }, [handleEdit, company.company_user_role, t])

  return (
    <>
      <DataGridAccordion
        items={[
          {
            title: t("view.Settings.CodesList.title"),
            columns,
            rows,
            addForm: {
              submitText: '',
              openButtonText: t("view.Settings.CodesList.openAddFormButtonText"),
              title: t("view.Settings.CodesList.addFormTitle"),
              form: CreateEditCode,
              props: {
                submit: submitCreate
              }
            },
          },
        ]}
      />

      <Dialog
        open={dialog === "edit"}
        onClose={closeDialog}
        title={t("view.Settings.CodesList.editDialogTitle")}
        maxWidth={"md"}
      >
        <CreateEditCode
          initialValues={createInitialValues(editCc)}
          submit={submitEdit}
          onCancel={closeDialog}
        />
      </Dialog>
    </>
  )
}
